import { collection, addDoc, query, where, orderBy, onSnapshot, updateDoc, doc, serverTimestamp, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase/config';

// 알림 생성
// 2026-08-06 보안 P1③: rules가 senderId==본인 + 내부 링크만 허용 (발신자 사칭·외부 피싱 링크 차단)
// 알림 실패가 본 작업(발행·주문 등)을 죽이지 않도록 throw하지 않는다 (회사 원칙)
export const createNotification = async (notificationData) => {
  try {
    const { recipientId, type, title, message, link, metadata = {} } = notificationData;
    await addDoc(collection(db, 'notifications'), {
      recipientId, type, title, message,
      link: link || '',
      metadata,
      senderId: auth.currentUser?.uid || null,
      read: false,
      createdAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) { console.error('알림 생성 실패(본 작업은 계속):', error); return { success: false, error }; }
};

// 알림 제목 표시 — 공정 3단계(2026-10-12 대표 "권고대로") 이름으로 맞춘다. 저장된 알림 문서는 바꾸지 않고 화면 표시만.
//   order_received  : 서버 onWorkOrderStageChange 가 이제 '의뢰서 확인'으로 보낸다. 그 전에 쌓인 '의뢰서 접수' 제목은 표시만 '의뢰서 확인'으로.
//   order_design_done: 신규 type — 제목 그대로('디자인 완료'). 비어 있으면 '디자인 완료'.
//   본문(message)은 저장값 그대로 둔다(구 본문 문구를 추측해 바꾸지 않는다).
export const notificationTitleOf = (n) => {
  const t = (n && n.title) || '';
  if (n && n.type === 'order_received' && (t === '의뢰서 접수' || !t)) return '의뢰서 확인';
  if (n && n.type === 'order_design_done' && !t) return '디자인 완료';
  return t;
};

// 알림 읽음 처리
export const markNotificationAsRead = async (notificationId) => {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), { read: true, readAt: serverTimestamp() });
    return { success: true };
  } catch (error) { console.error('알림 읽음 처리 실패:', error); throw error; }
};

// 사용자 알림 실시간 구독
export const subscribeToNotifications = (userId, callback) => {
  const q = query(collection(db, 'notifications'), where('recipientId', '==', userId), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => { callback(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); });
};

// 알림 타입별 생성 헬퍼 함수들
export const notifyOrderCreated = (recipientId, orderId, orderNumber, senderName) => createNotification({ recipientId, type: 'order_created', title: '새로운 주문', message: senderName ? senderName + '에서 새 주문이 도착했습니다.' : '주문번호 ' + orderNumber + '가 생성되었습니다.', link: '/view-order/' + orderId, metadata: { orderId, orderNumber, senderName } });

export const notifyOrderStatusChanged = (recipientId, orderId, orderNumber, status) => {
  const statusText = { 'pending': '대기중', 'approved': '승인됨', 'in_progress': '제작중', 'completed': '완료', 'cancelled': '취소됨' };
  return createNotification({ recipientId, type: 'order_status_changed', title: '주문 상태 변경', message: `주문 ${orderNumber}의 상태가 "${statusText[status]}"로 변경되었습니다.`, link: `/view-order/${orderId}`, metadata: { orderId, orderNumber, status } });
};

export const notifyPaymentCompleted = (recipientId, amount, orderNumber) => createNotification({ recipientId, type: 'payment_completed', title: '결제 완료', message: `${amount.toLocaleString()}원 결제가 완료되었습니다. (주문: ${orderNumber})`, link: `/payments`, metadata: { amount, orderNumber } });

export const notifyStaffJoinRequest = (recipientId, staffName, companyName) => createNotification({ recipientId, type: 'staff_join_request', title: '직원 가입 요청', message: `${staffName}님이 ${companyName}에 가입을 요청했습니다.`, link: `/company/staff-management`, metadata: { staffName, companyName } });

export const notifyStaffApproved = (recipientId, companyName) => createNotification({ recipientId, type: 'staff_approved', title: '직원 승인 완료', message: `${companyName}의 직원으로 승인되었습니다.`, link: `/company`, metadata: { companyName } });

// 모든 알림을 읽음으로 표시
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const snapshot = await getDocs(query(collection(db, 'notifications'), where('recipientId', '==', userId), where('read', '==', false)));
    if (snapshot.empty) { console.log('읽지 않은 알림이 없습니다.'); return; }
    await Promise.all(snapshot.docs.map(doc => updateDoc(doc.ref, { read: true, readAt: serverTimestamp() })));
    console.log(`✅ ${snapshot.size}개의 알림을 읽음 처리 완료`);
  } catch (error) { console.error('❌ 전체 읽음 처리 실패:', error); throw error; }
};

const NotificationSystem = { createNotification, markNotificationAsRead, subscribeToNotifications, notifyOrderCreated, notifyOrderStatusChanged, notifyPaymentCompleted, notifyStaffJoinRequest, notifyStaffApproved, markAllNotificationsAsRead };

export default NotificationSystem;